import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";

const CV_TYPES = ['Solo exhibition','Group exhibition','Residency','Award','Education','Publication','Other'];
const BLANK_CV = { type:'Group exhibition', title:'', venue:'', location:'', year:'' };

export function CvSection({ profile, onCvSaved }) {
  const [entries, setEntries] = useState(() => {
    try { return JSON.parse(profile?.cv || '[]'); } catch { return []; }
  });
  const [editing, setEditing] = useState(null); // null | 'new' | index
  const [form, setForm] = useState({ ...BLANK_CV });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ ...BLANK_CV }); setEditing('new'); };
  const openEdit = (i) => { setForm({ ...entries[i] }); setEditing(i); };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const updated = editing === 'new'
      ? [...entries, form]
      : entries.map((e,i) => i === editing ? form : e);
    const { error } = await supabase.from('profiles').update({ cv: JSON.stringify(updated) }).eq('id', profile.id);
    setSaving(false);
    if (error) return;
    setEntries(updated);
    onCvSaved(JSON.stringify(updated));
    setEditing(null);
  };

  const handleDelete = async (i) => {
    const updated = entries.filter((_,idx) => idx !== i);
    await supabase.from('profiles').update({ cv: JSON.stringify(updated) }).eq('id', profile.id);
    setEntries(updated);
    onCvSaved(JSON.stringify(updated));
  };

  const inp = { background:C.parch, border:`1px solid ${C.sand}`, borderRadius:6, padding:"8px 10px", fontSize:".82rem", color:C.bark, width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" };
  const lbl = { fontSize:".6rem", fontFamily:"'DM Mono',monospace", letterSpacing:"1px", textTransform:"uppercase", color:C.muted, display:"block", marginBottom:3 };

  // Group by type for display
  const grouped = CV_TYPES.reduce((acc, t) => {
    const items = entries.filter(e => e.type === t);
    if (items.length) acc[t] = items;
    return acc;
  }, {});
  const ungrouped = entries.filter(e => !CV_TYPES.includes(e.type));
  if (ungrouped.length) grouped['Other'] = ungrouped;

  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
        <span className="slbl" style={{ margin:0 }}>CV</span>
        <button className="btn-p" style={{ fontSize:".64rem",padding:"7px 14px",minHeight:36 }} onClick={openNew}>+ Add entry</button>
      </div>

      {editing !== null && (
        <div style={{ background:C.parchD,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",marginBottom:14 }}>
          <div style={{ marginBottom:10 }}>
            <label style={lbl}>Type</label>
            <select style={inp} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              {CV_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={lbl}>Title *</label>
            <input style={inp} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Exhibition or award name"/>
          </div>
          <div style={{ display:"flex",gap:8,marginBottom:10 }}>
            <div style={{ flex:2 }}>
              <label style={lbl}>Venue / Institution</label>
              <input style={inp} value={form.venue} onChange={e=>setForm(f=>({...f,venue:e.target.value}))} placeholder="Gallery or institution"/>
            </div>
            <div style={{ flex:1 }}>
              <label style={lbl}>Year</label>
              <input style={inp} value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))} placeholder="2023"/>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Location</label>
            <input style={inp} value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="City, State"/>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button className="btn-dk" style={{ flex:1,padding:"9px" }} onClick={()=>setEditing(null)} disabled={saving}>Cancel</button>
            <button className="btn-p" style={{ flex:2,padding:"9px" }} onClick={handleSave} disabled={saving||!form.title.trim()}>{saving?"Saving…":"Save entry"}</button>
          </div>
        </div>
      )}

      {entries.length === 0 && editing === null && (
        <div style={{ border:`2px dashed ${C.sand}`,borderRadius:8,padding:"28px 16px",textAlign:"center" }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:".95rem",color:C.bark,marginBottom:6 }}>No CV entries yet</div>
          <p style={{ fontSize:".78rem",color:C.muted,marginBottom:0,lineHeight:1.65 }}>Add your exhibition history, residencies, awards and education.</p>
        </div>
      )}

      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"2px",textTransform:"uppercase",color:C.muted,marginBottom:6 }}>{type}</div>
          {items.map((entry, globalIdx) => {
            const idx = entries.indexOf(entry);
            return (
              <div key={idx} style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 12px",background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,marginBottom:6 }}>
                <div>
                  <div style={{ fontSize:".84rem",color:C.bark,fontWeight:500,marginBottom:2 }}>{entry.title}</div>
                  <div style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>
                    {[entry.venue,entry.location,entry.year].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ display:"flex",gap:6,flexShrink:0,marginLeft:10 }}>
                  <button onClick={()=>openEdit(idx)} style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"3px 9px",fontSize:".68rem",color:C.muted,cursor:"pointer" }}>Edit</button>
                  <button onClick={()=>handleDelete(idx)} style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"3px 9px",fontSize:".68rem",color:C.muted,cursor:"pointer" }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
